import React, { FC, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import Button from '../../../components/Button/Button';
import {
  BULK_UPDATE_ACCEPTED_EXTENSION,
  BULK_UPDATE_MAX_FILE_SIZE_BYTES,
  BULK_UPDATE_MAX_ROWS,
  formatFileSize,
} from '../../../utils/bulkUpdateValidation';

type Props = {
  file: File | null;
  disabled?: boolean;
  onFileSelected: (file: File) => void;
};

const BulkUpdateDropzone: FC<Props> = ({ file, disabled, onFileSelected }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const handleFiles = (files: FileList | null) => {
    const selected = files?.[0];
    if (selected) {
      onFileSelected(selected);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);
    if (!disabled) {
      handleFiles(event.dataTransfer.files);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(true);
  };

  return (
    <Box
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={() => setIsDragActive(false)}
      sx={{
        p: 3,
        border: '1px dashed',
        borderColor: isDragActive ? '#456BD9' : '#c4c4c4',
        borderRadius: 1,
        backgroundColor: isDragActive ? 'rgba(69, 107, 217, 0.04)' : 'transparent',
        textAlign: 'center',
      }}
    >
      <Stack spacing={1} alignItems="center">
        <Icon icon="ph:microsoft-excel-logo" fontSize={32} color="#456BD9" />
        <Typography variant="body2">Drag and drop your file here, or</Typography>
        <Button variant="outlined" size="small" disabled={disabled} onClick={() => inputRef.current?.click()}>
          Browse file
        </Button>
        <input
          ref={inputRef}
          type="file"
          hidden
          accept={BULK_UPDATE_ACCEPTED_EXTENSION}
          data-testid="bulk-update-file-input"
          onChange={(event) => {
            handleFiles(event.target.files);
            event.target.value = '';
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {BULK_UPDATE_ACCEPTED_EXTENSION} only &bull; max {formatFileSize(BULK_UPDATE_MAX_FILE_SIZE_BYTES)} &bull; max{' '}
          {BULK_UPDATE_MAX_ROWS} rows
        </Typography>
        {file ? (
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {file.name} ({formatFileSize(file.size)})
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
};

export default BulkUpdateDropzone;
